<template>
  <div v-if="isAnimating" :class="['avatarContainer', position, size]">
    <div ref="animation" class="animation no-select" />
  </div>
  <div v-else class="audio-avatar-container" />
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, computed, nextTick } from 'vue'
import type { Ref } from 'vue'
import type {
  AvatarOptions,
  AvatarPosition,
  AvatarSize,
  MouthCue,
  AnimationTrigger,
  AvatarAnimation,
  AnimationTimeStamp,
  SceneAnimation,
  AudioPlayer,
  AudioLoadError,
  AvatarWalkingState,
  AvatarWalkingCoordinates,
  AvatarPathPoint,
  ManagedInterval
} from '@/customTypes'

// Services
import Logger from '@/services/Logger.js'

// Helpers
import { useTimeout, useInterval } from '@/utils/helpers'

// Stores
import { storeToRefs } from 'pinia'
import { useDataStore } from '@/stores/DataStore'
import { useEffectsStore } from '@/stores/EffectsStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { useProgressiveMapStore } from '@/stores/ProgressiveMapStore'
import { useDevStore } from '@/stores/DevStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'

// Plugins
import lottie from 'lottie-web'

const logger: any = new Logger('Avatar', true)
const audioLogger = new Logger('AudioAvatar', true)

const props = defineProps({
  options: {
    type: Object as () => AvatarOptions,
    default: () => ({ position: 'center', size: 'lg', audio: '' })
  },
  delay: {
    type: Number,
    default: 1
  },
  inSession: {
    type: Boolean,
    default: true
  },
  isAnimating: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits<{
  (e: 'avatar-finished'): void
  (e: 'walking-complete'): void
}>()

const dataStore = useDataStore()
const {
  screenerTemplateId,
  screenerAvatar,
  avatarVolumeLevel,
  isOffline,
  currentSubtest,
  starPopGameActive
} = storeToRefs(dataStore)
const effectsStore = useEffectsStore()
const soundEffectsStore = useSoundEffectsStore()
const devStore = useDevStore()
const progressiveMapStore = useProgressiveMapStore()
const activeScreenerStore = useActiveScreenerStore()

const {
  avatarAnimationState,
  animationTriggers,
  avatarAnimationQueue,
  sceneAnimationState,
  avatarTransitionCallback,
  backgroundImage,
  backgroundAnimationState,
  introOutroAnimations,
  sceneAnimations,
  avatarAnimations,
  avatarDefaultIdleAnimations
} = storeToRefs(effectsStore)

const { setManagedTimeout, createManagedDebounce } = useTimeout()
const { setManagedInterval, clearManagedInterval } = useInterval()

// Common state
const currentFileName: Ref<string> = ref('')
const audioDuration: Ref<number> = ref(0)
const audioCurrentTime: Ref<number> = ref(0)
const audioPlayer: Ref<AudioPlayer | null> = ref(null)
const currentOptions: Ref<AvatarOptions> = ref(props.options)
const isTransitioning: Ref<boolean> = ref(false)
const emitterFlag: Ref<boolean> = ref(false)
let audioLoadError: number = 0

// Animating avatar specific state
const animationWatchersSetup = ref<Boolean>(false)
const mouthCues: Ref<MouthCue[]> = ref([])
const currentMouthCue: Ref<string> = ref('A')
const animationData: Ref<string> = ref('')
const visemeOpacity: Ref<{ [key: string]: number }> = ref({
  A: 1,
  D: 0,
  B: 0,
  C: 0,
  G: 0,
  H: 0,
  X: 0,
  E: 0,
  F: 0
})

const animateBlink: Ref<number> = ref(0)
const animation: Ref<HTMLDivElement | null> = ref(null)
const nextAnimation: Ref<AnimationTimeStamp | null> = ref(null)
const waitingForBreakpoint: Ref<boolean> = ref(false)
const animationBreakPoint: Ref<number | null> = ref(null)
const avatarTalking: Ref<boolean> = ref(false)
const avatarWaitingToTalk: Ref<boolean> = ref(false)
const avatarAnimationPlaying: Ref<boolean> = ref(false)
const currentAnimation: Ref<AvatarAnimation | null> = ref(null)
const currentTimestamps: Ref<AnimationTimeStamp | null> = ref(null)
const position: Ref<AvatarPosition> = ref(currentOptions.value.position)
const nextPosition: Ref<AvatarPosition | null> = ref(null)
const size: Ref<AvatarSize> = ref(currentOptions.value.size)
const nextSize: Ref<AvatarSize | null> = ref(null)
const avatarFirstLoad: Ref<boolean> = ref(true)
const animationLoaded: Ref<boolean> = ref(false)
const anim = ref<any>(null)

const avatarFeetCoordinates = ref<{ x: any; y: any }>({
  x: '50%',
  y: '80%'
})

const walkingState = ref<AvatarWalkingState>({
  isWalking: false,
  currentPointIndex: 0,
  pathPoints: [],
  walkingDirection: 'right',
  reachedDestination: false,
  currentProgress: 0
})

const blinkAnimationFrame = 12
let framesSinceLastBlink: number = 0
let blinkCooldownFrames: number = 300
let audioAnimationFrameId: number | undefined

onMounted(async () => {
  watch(
    () => props.isAnimating,
    async (animating) => {
      if (animating && !anim.value) {
        if (!animationWatchersSetup.value) animationWatchers()
        await setAvatar()
        loadAnimation(animationData.value)
      }
    },
    { immediate: true }
  )
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (props.isAnimating) {
    anim.value?.removeEventListener('onEnterFrame', onEnterFrame)
    anim.value?.removeEventListener('complete', onAnimationComplete)
    anim.value?.removeEventListener('DOMLoaded', onAnimationLoaded)

    anim.value?.destroy()
    anim.value = null
    walkingState.value.isWalking = false
  }
  stopCurrent()
})

const avatarFinished = computed(
  () => !avatarTalking.value && !avatarAnimationPlaying.value && !avatarWaitingToTalk.value
)

const isIntroOrOutro = computed(() => activeScreenerStore.isIntroOrOutro())

// Note: Watchers
watch(avatarFinished, (readyToMoveOn) => {
  if (emitterFlag.value) return
  if (readyToMoveOn) {
    if (audioPlayer.value?.id.match(/^S\d{1,2}/)) {
      audioPlayer.value.remove()
    }
    audioPlayer.value = null
    emitterFlag.value = true
    emit('avatar-finished')
    if (props.isAnimating) {
      updateVisemeOpacity('A')
    }
  }
})

watch(isOffline, async (newValue, oldValue) => {
  // Proceed only when reconnecting (from offline to online)
  if (!oldValue || newValue) return

  const { audio } = currentOptions.value
  const audioPlayerNotPlaying = !audioPlayer.value?.playing()
  const audioErrorExists = audioLoadError > 0

  if (audioPlayerNotPlaying && audioErrorExists && audio) {
    try {
      if (audioLoadError) {
        const newAudioID = audio?.split('.')[0]
        if (newAudioID) {
          soundEffectsStore.clearLoadErrorTimeout(newAudioID)
        }
      }
      await setNewAudio(audio)

      // Start avatar talking animation if not already playing
      if (props.isAnimating && !avatarAnimationPlaying.value) {
        avatarTalking.value = true
        startAvatarTalking()
      }
    } catch (error) {
      logger.error('Error setting new audio after network reconnect', error)
    }
  }
})

const animationWatchers = () => {
  // Next animation set from queue and waits for breaking point or end of current animation to transition
  watch(avatarAnimationQueue, (updatedQueue) => {
    if (updatedQueue.length === 0) return

    const firstAnimation = updatedQueue[0]
    if (
      firstAnimation === currentAnimation.value ||
      firstAnimation === nextAnimation.value?.animation
    ) {
      effectsStore.processNextAnimation()
      return
    }

    if (!avatarDefaultIdleAnimations.value.includes(firstAnimation)) {
      avatarAnimationPlaying.value = true
    }

    const nextAnim = effectsStore.getAnimation(firstAnimation)
    if (!nextAnim) {
      if (avatarDefaultIdleAnimations.value.length) {
        nextAnimation.value = effectsStore.getAnimation(avatarDefaultIdleAnimations.value[0])
      }
      return
    }

    nextAnimation.value = nextAnim
    waitingForBreakpoint.value = true
    anim.value.loop = false
  })

  watch(avatarWaitingToTalk, (waiting) => {
    if (!waiting) {
      logger.info('Finished waiting to talk')
      startAvatarTalking()
    }
  })
  animationWatchersSetup.value = true
}

// Methods
const updateOptions = (updatedOptions: any) => {
  currentOptions.value = updatedOptions
  logger.info('updated options:', updatedOptions)
  if (props.isAnimating) {
    if (animationLoaded.value) {
      setupAvatarConfig(updatedOptions)
    } else {
      logger.info('Options changes before animation loaded')
    }
  } else {
    setupAudioConfig(updatedOptions)
  }
}

const setupAvatarConfig = async (options: AvatarOptions) => {
  try {
    if (options.position !== position.value || options.position !== nextPosition.value) {
      updateAvatarOptions(options)
    } else {
      if (
        backgroundImage.value.current !== backgroundImage.value.next &&
        backgroundImage.value.next
      ) {
        avatarWaitingToTalk.value = true
        logger.info('Forcing transition out due to background change')
        updateAvatarOptions(options, true)
      } else {
        if (avatarTransitionCallback.value.after) {
          logger.info('Running callback after avatar relocation')
          avatarTransitionCallback.value.after()
        }
        if (avatarTransitionCallback.value.during) {
          logger.info('Running callback during avatar relocation')
          avatarTransitionCallback.value.during()
        }
      }
    }
    if (options.audio === currentFileName.value) return
    stopCurrent()

    await setNewAudio(options.audio)
    if (!options.audio) {
      avatarTalking.value = false
      return
    }

    if (avatarWaitingToTalk.value) return

    if (!avatarAnimationPlaying.value) {
      avatarTalking.value = true
      startAvatarTalking()
    } else {
      avatarWaitingToTalk.value = true
      avatarTalking.value = true
    }
  } catch (error) {
    logger.error('Error updating avatar config:', error)
  }
}

const setupAudioConfig = async (options: AvatarOptions) => {
  try {
    if (options.audio === currentFileName.value) return
    stopCurrent()

    await setNewAudio(options.audio)
    if (!options.audio) return

    handleTransition()
  } catch (error) {
    logger.error('Error updating audio config:', error)
  }
}

// Sets next animation after animation has been completed
// eslint-disable-next-line max-statements
const onAnimationComplete = () => {
  if (!props.isAnimating || !anim.value) return

  if (walkingState.value.isWalking) {
    // If avatar is walking, replay the walking animation
    if (!currentTimestamps.value) return
    anim.value.playSegments([currentTimestamps.value.start, currentTimestamps.value.end], true)
    return
  }

  if (nextAnimation.value) {
    effectsStore.processNextAnimation()
    logger.info('Starting animation for:', nextAnimation.value)

    // Check if there is a walking animation playing and starts the movement along the path
    if (nextAnimation.value.animation.startsWith('walking')) {
      const container = animation.value?.parentElement
      if (container) {
        moveAlongPath()
        walkingState.value.isWalking = true
      }
    }

    if (!effectsStore.isTalkDisabledAnimation(avatarAnimationState.value)) {
      // Allow avatar to talk
      avatarAnimationPlaying.value = false
      avatarWaitingToTalk.value = false
      if (avatarDefaultIdleAnimations.value.includes(avatarAnimationState.value)) {
        anim.value.loop = true
      }
    } else {
      avatarAnimationPlaying.value = true
      anim.value.loop = false
    }

    // If previous animation was a transition In set transition state to false
    if (effectsStore.isInTransition(currentAnimation.value as AvatarAnimation)) {
      if (avatarTransitionCallback.value.after) {
        logger.log('Running callback after avatar relocation')
        avatarTransitionCallback.value.after()
      }
    }

    currentTimestamps.value = nextAnimation.value
    // Set current animation to next animation
    currentAnimation.value = nextAnimation.value.animation as AvatarAnimation
    if (effectsStore.isInTransition(currentAnimation.value)) {
      if (avatarTransitionCallback.value.during) {
        logger.log('Running callback during avatar relocation')
        const container = animation.value?.parentElement
        if (container && walkingState.value.reachedDestination) {
          container.style.transition = 'none'
          container.style.transform = 'none' // removes any animations present
          container.style.removeProperty('transform')
          cleanupCurrentAnimation()
        }

        avatarTransitionCallback.value.during()
      }
      if (nextPosition.value !== null) position.value = nextPosition.value
      if (nextSize.value !== null) size.value = nextSize.value
    }

    // Playing animation
    anim.value.playSegments([nextAnimation.value.start, nextAnimation.value.end], true)

    // Playing audio for animation
    if (nextAnimation.value.audio && props.inSession) {
      soundEffectsStore.playTrack(nextAnimation.value.animation)
    }

    // If transitioning out set next animation to transition in else default to idle state
    if (!effectsStore.isOutTransition(nextAnimation.value.animation)) {
      effectsStore.queueAvatarAnimation(effectsStore.getDefaultIdleAnimation(), false)
      animationBreakPoint.value = nextAnimation.value.break || null
    } else {
      effectsStore.queueAvatarAnimation(effectsStore.getTransitionAnimation('IN'), true)
      animationBreakPoint.value = nextAnimation.value.break || null
    }
    nextAnimation.value = null
  }
  waitingForBreakpoint.value = false
}

// Loads avatar animation in a teleportation/transition state when in a session, otherwise it defaults to idle
const loadAnimation = (newAnimationData: any) => {
  if (!props.isAnimating) return
  let loadAnimation = effectsStore.getAnimation(effectsStore.getDefaultIdleAnimation())

  avatarWaitingToTalk.value = true
  currentAnimation.value = effectsStore.getTransitionAnimation('IN')
  effectsStore.queueAvatarAnimation(currentAnimation.value, true)
  loadAnimation = effectsStore.getAnimation(currentAnimation.value)

  if (!loadAnimation) {
    if (avatarDefaultIdleAnimations.value.length) logger.error('No default idle animations found')
    logger.error(
      `Loading: Animation "${currentAnimation.value}" not found, attempting to set default idle animation`
    )
    loadAnimation = effectsStore.getAnimation(avatarDefaultIdleAnimations.value[0])
    return
  }
  if (avatarAnimationState.value[0]) {
    avatarAnimationPlaying.value = true
    anim.value = lottie.loadAnimation({
      container: animation.value as HTMLDivElement,
      loop: avatarDefaultIdleAnimations.value.includes(currentAnimation.value),
      autoplay: false,
      animationData: newAnimationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice'
      },
      initialSegment: [loadAnimation.start, loadAnimation.end]
    })

    anim.value.addEventListener('enterFrame', onEnterFrame)
    anim.value.addEventListener('complete', onAnimationComplete)
    anim.value.addEventListener('DOMLoaded', onAnimationLoaded)

    if (animation.value) {
      animation.value.querySelector('svg')?.removeAttribute('width')
      animation.value.querySelector('svg')?.removeAttribute('height')
    }
  }
}

const onEnterFrame = () => {
  if (!props.isAnimating) return

  if (waitingForBreakpoint.value && !isNaN(animationBreakPoint.value ?? 0)) {
    if (parseInt(anim.value.currentFrame) === animationBreakPoint.value) {
      // Reset breakpoint to stop it from firing multiple times
      waitingForBreakpoint.value = false
      onAnimationComplete()
    }
  }
  framesSinceLastBlink++

  if (framesSinceLastBlink > blinkAnimationFrame) toggleBlink()
}

// Once avatar has loaded it uses the config to trigger it's first animation
const onAnimationLoaded = () => {
  logger.info('Avatar loaded')
  animationLoaded.value = true
  setupAvatarConfig(currentOptions.value)
}

// Checks the avatar can talk and starts the audio playing
const startAvatarTalking = async () => {
  if (!props.isAnimating) return

  logger.info('Attempting to start avatar talking')
  if (!currentOptions.value.audio) return
  if (currentOptions.value.position !== position.value) {
    logger.info('Repositioning avatar instead of talking')
    if (currentOptions.value.position !== nextPosition.value)
      setupAvatarConfig(currentOptions.value)
  }
  await setVisemeData(currentOptions.value.audio)
  play()
}

// Updates the avatar's position and size using transitions animations
const updateAvatarOptions = (newPlacement: AvatarOptions, forcedTeleport: boolean = false) => {
  if (!props.isAnimating) return

  if (newPlacement.position === position.value && !avatarFirstLoad.value && !forcedTeleport) return
  avatarWaitingToTalk.value = true
  avatarTalking.value = true
  nextPosition.value = newPlacement.position
  nextSize.value = newPlacement.size
  if (avatarFirstLoad.value) {
    avatarFirstLoad.value = false
    const playingAnimation = effectsStore.getAnimation(currentAnimation.value as string)

    anim.value.play()
    if (playingAnimation?.audio && props.inSession) {
      soundEffectsStore.playTrack(currentAnimation.value as string)
    }

    effectsStore.queueAvatarAnimation(effectsStore.getDefaultIdleAnimation(), false)
    if (avatarTransitionCallback.value.during) {
      logger.log('Running callback during avatar relocation')
      avatarTransitionCallback.value.during()
    }
  } else {
    effectsStore.queueAvatarAnimation(effectsStore.getTransitionAnimation('OUT'), true)
    if (walkingState.value?.reachedDestination) {
      window.removeEventListener('resize', handleResize)
    }
  }
}

//Changes the mouth shape to match the viseme data and runs animations for the current time stamp of audio
const updateMouthCue = () => {
  if (!props.isAnimating) return

  for (const cue of mouthCues.value) {
    if (audioCurrentTime.value >= cue.start && audioCurrentTime.value < cue.end) {
      updateVisemeOpacity(cue.value)
      break
    }
  }
}

const updateVisemeOpacity = (newValue: string) => {
  const oldValue = currentMouthCue.value
  if (newValue === oldValue) return
  visemeOpacity.value[newValue] = 1
  visemeOpacity.value[oldValue] = 0
  currentMouthCue.value = newValue
}

// Animation trigger listener that listens for animation triggers and queues them up
const animationTriggerListener = () => {
  let index = 0

  const shouldQueueAvatarAnimation = (trigger: AnimationTrigger) => {
    return (
      props.isAnimating &&
      avatarAnimations.value.includes(trigger.animation) &&
      !avatarAnimationQueue.value.includes(trigger.animation) &&
      avatarAnimationState.value !== trigger.animation &&
      currentAnimation.value !== trigger.animation
    )
  }

  const shouldSetSceneAnimation = (trigger: AnimationTrigger) => {
    return (
      sceneAnimations.value.includes(trigger.animation) &&
      sceneAnimationState.value !== trigger.animation &&
      isIntroOrOutro.value
    )
  }

  const shouldSetBackgroundAnimation = (trigger: AnimationTrigger) => {
    return (
      introOutroAnimations.value.includes(trigger.animation) &&
      backgroundAnimationState.value !== trigger.animation &&
      isIntroOrOutro.value
    )
  }

  for (const trigger of animationTriggers.value as AnimationTrigger[]) {
    if (audioCurrentTime.value >= trigger.start && audioCurrentTime.value < trigger.end) {
      if (trigger.animation) {
        if (shouldQueueAvatarAnimation(trigger)) {
          effectsStore.queueAvatarAnimation(trigger.animation, false)
          effectsStore.removeAnimationTrigger(index)
        } else if (shouldSetSceneAnimation(trigger)) {
          effectsStore.setSceneAnimationState(trigger.animation as SceneAnimation)
          effectsStore.removeAnimationTrigger(index)
        } else if (shouldSetBackgroundAnimation(trigger)) {
          effectsStore.setBackgroundAnimationState(trigger.animation)
          effectsStore.removeAnimationTrigger(index)
        }
      }
      index++
      break
    }
  }
}

function stopCurrent(): void {
  if (audioPlayer.value) {
    audioPlayer.value.end()
  }
}

async function setAvatar() {
  if (!props.isAnimating || animationData.value) return

  if (screenerAvatar.value) {
    animationData.value = screenerAvatar.value
  } else {
    logger.error('No avatar data loaded when attempting to load avatar')
  }
}

async function setVisemeData(fileName: string): Promise<void> {
  if (!props.isAnimating) return

  try {
    const fileNameWithJson = fileName.split('.')[0] + '.json'
    // get json file from assets bucket
    const response = await fetch(
      `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/avatar-viseme/${fileNameWithJson}?v=${_ASSETS_VERSION_}`
    )

    if (!response.ok) return
    const data = await response.json()
    mouthCues.value = data.mouthCues
    audioDuration.value = data.metadata.duration
  } catch (e) {
    logger.error('Error fetching viseme data', e)
  }
}

function updatePlayerTime(lastTime: number = 0): void {
  if (!audioPlayer.value || typeof audioPlayer.value.seek !== 'function') {
    if (audioAnimationFrameId) {
      cancelAnimationFrame(audioAnimationFrameId)
      audioAnimationFrameId = undefined
    }
    return
  }

  const updatedTimestamp = audioPlayer.value.seek() || 0

  if (audioPlayer.value.playing()) {
    audioAnimationFrameId = requestAnimationFrame(() => updatePlayerTime(audioCurrentTime.value))
  }

  // Only update the mouth and animations if 0.1 seconds has passed by.
  if (updatedTimestamp - lastTime >= 0.1 || lastTime === 0) {
    audioCurrentTime.value = Math.round(updatedTimestamp * 100) / 100
    if (props.isAnimating) {
      updateMouthCue()
    } else {
      dataStore.updateQuestionTimestamp(audioCurrentTime.value)
    }
    animationTriggerListener()
  }
}

async function setNewAudio(url: string): Promise<void> {
  return new Promise(async (resolve, reject) => {
    if (!url) return
    emitterFlag.value = false
    isTransitioning.value = false // Reset transitioning state
    const newAudioID = url?.split('.')[0]
    currentFileName.value = url

    try {
      const audioUrl = `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/avatar-audio/${url}?v=${_ASSETS_VERSION_}`
      const audioTrackData = {
        id: newAudioID,
        name: `Avatar ${newAudioID}`,
        url: audioUrl,
        volume: avatarVolumeLevel.value,
        loop: false
      }

      audioLogger.info('Setting new audio', newAudioID, audioUrl, audioTrackData)
      audioPlayer.value = await soundEffectsStore.createPlayerForTrack(audioTrackData)

      audioPlayer.value.on('play', () => {
        audioLoadError = 0
        updatePlayerTime()
      })

      audioPlayer.value.on('end', () => {
        if (props.isAnimating) {
          if (!avatarTalking.value && avatarFinished.value) {
            // NOTE: If audio starts with S followed by a number for subtest remove audio track
            if (audioPlayer.value?.id.match(/^S\d{1,2}/)) {
              audioPlayer.value.remove()
            }
            audioPlayer.value = null
            updateVisemeOpacity('A')
          }
          avatarTalking.value = false
          cancelAnimationFrame(audioAnimationFrameId!)
        } else {
          emit('avatar-finished')
        }
      })

      resolve()
    } catch (e) {
      if ((e as AudioLoadError).name === 'AudioLoadError') {
        const error = e as AudioLoadError
        soundEffectsStore.startLoadErrorTimeout(error.error, newAudioID)
        audioLoadError = 1
      }
      reject('Issue creating new player from url')
    }
  })
}

const play = (): void => {
  if (audioPlayer.value) {
    audioLogger.info('Attempting to play audio', audioPlayer.value)
    setManagedTimeout(() => {
      audioPlayer.value!.start()
    }, props.delay * 1000)
  }
}

const toggleBlink = () => {
  if (!props.isAnimating) return

  const randomValue = Math.random()

  if (randomValue >= 0.98 && framesSinceLastBlink >= blinkCooldownFrames) {
    animateBlink.value = 1
    framesSinceLastBlink = 0
  } else animateBlink.value = 0
}

const stop = () => {
  if (audioPlayer.value) {
    cancelAnimationFrame(audioAnimationFrameId!)
    audioPlayer.value.stop()
    audioPlayer.value = null
    isTransitioning.value = false
  }
  if (props.isAnimating && anim) {
    anim.value.stop()
    avatarTalking.value = false
    avatarWaitingToTalk.value = false
    avatarAnimationPlaying.value = false
    updateVisemeOpacity('A')
  }
}

// None Animating Avatar
const handleTransition = () => {
  if (props.isAnimating) return

  if (!isTransitioning.value) {
    isTransitioning.value = true
    handleTransitionDuring()
    // Small pause similar to the time it would take for the avatar transitioning to give a breather
    setManagedTimeout(() => {
      handleTransitionAfter()
      play()
    }, 1000)
  }
}

const handleTransitionDuring = () => {
  if (props.isAnimating) return

  if (avatarTransitionCallback.value.during) {
    audioLogger.info('Running callback during transition')
    avatarTransitionCallback.value.during()
  }
}

const handleTransitionAfter = () => {
  if (props.isAnimating) return

  if (avatarTransitionCallback.value.after) {
    audioLogger.info('Running callback after transition')
    avatarTransitionCallback.value.after()
  }
  isTransitioning.value = false
}

// works out the distance of the path since the path isn't necessarily straight we have to take into account each point used to calculate the speed of the keyframes
const calculateRemainingDistance = (points: Array<AvatarPathPoint>): number => {
  let distance = 0
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x
    const dy = points[i].y - points[i - 1].y
    distance += Math.sqrt(dx * dx + dy * dy)
  }
  return distance
}

// Updates the x an y positions of the avatar container
const updateContainerPosition = async (
  container: HTMLElement,
  x: number,
  y: number,
  withTransition: boolean = true
) => {
  container.style.transition = withTransition ? 'transform 0.3s linear' : 'transform 0.0s linear'
  container.style.transform = `translate3d(calc(${x}px - ${avatarFeetCoordinates.value.x}), calc(${y}px - ${avatarFeetCoordinates.value.y}), 1px)`

  if (withTransition) {
    await new Promise((resolve) => setManagedTimeout(resolve, 100))
  }
}

// Maps the remaining points to walk into keyframes
const createKeyframes = (remainingPoints: Array<AvatarPathPoint>) => {
  return remainingPoints.map((point) => ({
    transform: `translate3d(calc(${point.x}px - ${avatarFeetCoordinates.value.x}), calc(${point.y}px - ${avatarFeetCoordinates.value.y}), 1px)`
  }))
}

// Tracks progress along the path so if the page is resized it can proceed from the same point
const trackAnimationProgress = (remainingPoints: Array<AvatarPathPoint>, totalDuration: number) => {
  const totalKeyframes = remainingPoints.length
  const intervalTime = (totalDuration / totalKeyframes) * 1000
  let currentKeyframeIndex = 0
  const intervalRef = ref<ManagedInterval | null>(null)

  const updateProgress = () => {
    if (currentKeyframeIndex < totalKeyframes) {
      walkingState.value.currentProgress = (currentKeyframeIndex + 1) / totalKeyframes
      currentKeyframeIndex++

      const currentPoint = remainingPoints[currentKeyframeIndex]
      progressiveMapStore.setProgressiveMapFlipZone(currentPoint?.inFlipZone)
    } else {
      if (intervalRef.value) clearManagedInterval(intervalRef)
      intervalRef.value = null
    }
  }

  intervalRef.value = setManagedInterval(updateProgress, intervalTime)
}

// Sets the avatar to return to an idle state after reaching the destination
const handleAnimationFinish = (effectsStore: any, anim: any, onAnimationComplete: () => void) => {
  walkingState.value.isWalking = false
  walkingState.value.reachedDestination = true
  walkingState.value.currentProgress = 1
  currentPathAnimation.cancel()
  if (animation.value && animation.value.parentElement) {
    updateFinalPosition(animation.value.parentElement)
  }

  if (anim.value && anim.value.stop) {
    anim.value.stop()
  }
  nextTick(() => {
    effectsStore.queueAvatarAnimation(effectsStore.getDefaultIdleAnimation(), true)
    onAnimationComplete()
  })

  emit('walking-complete')
}

let currentPathAnimation: any = null

// Walking animation Path Logic sets the keyframes for the animation to follow from one point to the next until it reaches the destination
const updateAnimationPath = async (container: HTMLElement) => {
  if (currentPathAnimation) cleanupCurrentAnimation()
  const currentIndex = Math.max(1, walkingState.value.currentPointIndex)
  const pathPoints = progressiveMapStore.getAvatarPathSegment(currentIndex, currentIndex + 1)
  devStore.createTempDevPath(pathPoints)

  if (!pathPoints) {
    logger.error('No path segment found for current position', currentIndex)
    return
  }

  const totalPoints = pathPoints.length
  const progressPointIndex = Math.floor(walkingState.value.currentProgress * totalPoints)
  const remainingPoints = pathPoints.slice(progressPointIndex)

  if (remainingPoints.length === 0) {
    updateFinalPosition(container)
    return
  }

  if (progressPointIndex > 0) {
    const { x, y } = remainingPoints[0]
    await updateContainerPosition(container, x, y, false)
  }

  container.style.transition = 'none'

  // Calculate remaining distance and duration for keeping track of progress
  const remainingDistance = calculateRemainingDistance(remainingPoints)
  const baseSpeed = 90 // pixels per second
  const fullDuration = remainingDistance / baseSpeed

  const keyframes = createKeyframes(remainingPoints)
  // Animate the avatar along the remaining path
  currentPathAnimation = container.animate(keyframes, {
    duration: fullDuration * 1000,
    easing: 'linear',
    fill: 'forwards'
  })

  trackAnimationProgress(remainingPoints, fullDuration)

  currentPathAnimation.onfinish = () =>
    handleAnimationFinish(effectsStore, anim, onAnimationComplete)
}

// Updates the Final Position of the avatar after reaching the destination if the window is resized and no longer walking
const updateFinalPosition = (container: HTMLElement) => {
  nextTick(() => {
    const pathSegment = progressiveMapStore.getAvatarPathEndpoint()
    if (!pathSegment) {
      logger.warn("couldn't find updated final position")
      return
    }

    logger.log('Updating final position to: ', pathSegment.x, pathSegment.y)
    updateContainerPosition(container, pathSegment.x, pathSegment.y, false)
  })
}

// Handles the core walking animation logic calling functions as required depending on the state of the avatar
const moveAlongPath = async () => {
  if (!animation.value) return

  const container = animation.value.parentElement
  if (!container) return

  try {
    container.getAnimations().forEach((anim) => anim.cancel())
    if (!walkingState.value.isWalking) {
      container.style.transition = 'none'
      walkingState.value.currentProgress = 0
    }

    walkingState.value.isWalking = true
    await updateAnimationPath(container)

    window.addEventListener('resize', handleResize)
  } catch (error) {
    walkingState.value.isWalking = false
    walkingState.value.currentProgress = 0
    effectsStore.queueAvatarAnimation(effectsStore.getDefaultIdleAnimation(), false)
  }
}

// Handles the resize event for the walking animation updating the path and final position of the avatar
const handleResize = createManagedDebounce(() => {
  if (starPopGameActive.value) {
    window.removeEventListener('resize', handleResize)
    return
  }
  if (!animation.value?.parentElement) return
  if (walkingState.value.isWalking) {
    updateAnimationPath(animation.value.parentElement)
  } else {
    updateFinalPosition(animation.value.parentElement)
  }
}, 100)

// Initial setup for the walking animation teleporting the avatar in at it's starting location
const setupWalkingPositions = async (coordinates: AvatarWalkingCoordinates) => {
  if (!animation.value) return

  const container = animation.value.parentElement
  if (!container) return

  // Reset walking state
  walkingState.value = {
    isWalking: false,
    currentPointIndex: currentSubtest.value?.index ?? 1,
    pathPoints: coordinates.pathPoints,
    walkingDirection: coordinates.targetX! > coordinates.startX! ? 'right' : 'left',
    reachedDestination: false,
    currentProgress: 0
  }
  //Sets avatar location to starting position
  container.style.transition = 'none'
  container.style.transform = `translate3d(calc(${coordinates.startX}px - ${avatarFeetCoordinates.value.x}), calc(${coordinates.startY}px - ${avatarFeetCoordinates.value.y}), 1px)`
  position.value = 'fixed'
  size.value = 'xs'

  // Queue walking animation after teleporting into the scene setting the direction of the walking animation to play
  effectsStore.queueAvatarAnimation(effectsStore.getTransitionAnimation('IN'), true)
  const direction = walkingState.value.walkingDirection
  effectsStore.queueAvatarAnimation(
    `walking${direction.charAt(0).toUpperCase() + direction.slice(1)}`,
    true
  )
}

const cleanupCurrentAnimation = () => {
  if (currentPathAnimation) {
    currentPathAnimation.cancel()
    currentPathAnimation.onfinish = null
    currentPathAnimation = null
  }
}

defineExpose({
  updateOptions,
  stop,
  setupWalkingPositions
})
</script>

<style lang="scss">
.avatarContainer {
  display: block;
  box-sizing: content-box;
  pointer-events: none;
  will-change: bottom, left, height, width;
  z-index: 1000;

  .animation {
    height: 100%;
    width: 100%;
  }

  &.center {
    position: fixed;
    bottom: 5%;
    left: 0;
    display: flex;
    justify-content: center;
    width: 100%;
  }

  &.bottom {
    position: fixed;
    bottom: -7%;
    left: 0;
    display: flex;
    justify-content: center;
    width: 100%;
  }

  &.bottom-left {
    position: fixed;
    bottom: -4%;
    left: -4%;
    display: flex;
    width: 100%;
  }

  &.bottom-right {
    position: fixed;
    bottom: -5%;
    right: -5%;
    display: flex;
    justify-content: end;
    width: 100%;
  }

  &.dynamic {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &.fixed {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1010;
    transform: translateZ(1px);
  }

  &.hidden {
    visibility: hidden;
  }

  &.height {
    height: 100%;
  }

  &.lg {
    .animation {
      max-height: calc(var(--full-vh) * 0.9) !important;
      max-width: calc(var(--full-vh) * 0.9 - 70px) !important;
    }
  }

  &.md {
    .animation {
      max-height: calc(var(--full-vh) * 0.75) !important;
      max-width: calc(var(--full-vh) * 0.75 - 70px) !important;
    }
  }

  &.sm {
    .animation {
      max-height: calc(var(--full-vh) * 0.5) !important;
      max-width: calc(var(--full-vh) * 0.5 - 70px) !important;
    }
  }

  &.xs {
    .animation {
      max-height: calc(var(--full-vh) * 0.3) !important;
      max-width: calc(var(--full-vh) * 0.3 - 70px) !important;
    }
  }
}

.floor-shadow {
  opacity: 0.15;
  path {
    fill: #000000;
  }
}

.viseme-A {
  opacity: v-bind('visemeOpacity.A');
}

.viseme-D {
  opacity: v-bind('visemeOpacity.D');
}

.viseme-B {
  opacity: v-bind('visemeOpacity.B');
}

.viseme-C {
  opacity: v-bind('visemeOpacity.C');
}

.viseme-G {
  opacity: v-bind('visemeOpacity.G');
}

.viseme-H {
  opacity: v-bind('visemeOpacity.H');
}

.viseme-X {
  opacity: v-bind('visemeOpacity.X');
}

.viseme-E {
  opacity: v-bind('visemeOpacity.E');
}

.viseme-F {
  opacity: v-bind('visemeOpacity.F');
}

.blink {
  opacity: v-bind('animateBlink');
}
</style>
